/**
 * Booking Time Utilities
 * Safe functions for calculating booking-to-pickup timing metrics
 */

/**
 * Calculate time difference between booking creation and driver pickup
 * @param {Object} bookingData - The booking object
 * @returns {Object} - Time calculation result with safety checks
 */
export const calculateBookingToPickupTime = bookingData => {
  try {
    // Safety checks for required data
    if (!bookingData) {
      return {
        error: true,
        message: 'No booking data provided',
        displayValue: 'N/A',
      };
    }

    const { bookingDate, startTime, status } = bookingData;

    // Check if booking has been picked up
    if (!startTime || !['STARTED', 'REACHED', 'COMPLETE'].includes(status)) {
      if (['NEW', 'ACCEPTED', 'ARRIVED'].includes(status)) {
        return {
          error: false,
          isPending: true,
          displayValue: 'Pending Pickup',
          status: status,
          color: '#FFDE59', // YELLOW
        };
      }
      return {
        error: false,
        displayValue: 'N/A',
        color: '#d6d6d6', // BORDER_BACKGROUND
      };
    }

    // Validate timestamps
    const bookingTime = new Date(bookingDate);
    const pickupTime = new Date(startTime);

    if (isNaN(bookingTime.getTime()) || isNaN(pickupTime.getTime())) {
      return {
        error: true,
        message: 'Invalid timestamp data',
        displayValue: 'Invalid Data',
      };
    }

    // Calculate difference
    const diffMs = pickupTime.getTime() - bookingTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    // Handle negative times (shouldn't happen but safety first)
    if (diffMins < 0) {
      return {
        error: true,
        message: 'Pickup time before booking time',
        displayValue: 'Data Error',
      };
    }

    // Format display value
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    const displayValue =
      diffHours > 0 ? `${diffHours}h ${remainingMins}m` : `${diffMins}m`;

    // Determine color based on performance (configurable thresholds)
    const getPerformanceColor = minutes => {
      if (minutes <= 15) return '#0CAB03'; // GREEN - Excellent
      if (minutes <= 30) return '#FFDE59'; // YELLOW - Good
      if (minutes <= 60) return '#ff914b'; // ORANGE - Fair
      return '#FF1E00'; // RED - Poor
    };

    return {
      error: false,
      diffMs,
      diffMins,
      diffHours,
      displayValue,
      color: getPerformanceColor(diffMins),
      performance:
        diffMins <= 15
          ? 'Excellent'
          : diffMins <= 30
            ? 'Good'
            : diffMins <= 60
              ? 'Fair'
              : 'Poor',
    };
  } catch (error) {
    console.error('Error calculating booking to pickup time:', error);
    return {
      error: true,
      message: error.message,
      displayValue: 'Calculation Error',
    };
  }
};

/**
 * Get analytics summary for multiple bookings
 * @param {Array} bookings - Array of booking objects
 * @returns {Object} - Analytics summary
 */
export const getBookingTimeAnalytics = bookings => {
  if (!Array.isArray(bookings) || bookings.length === 0) {
    return {
      totalBookings: 0,
      completedBookings: 0,
      averagePickupTime: 0,
      performance: {},
    };
  }

  const completedBookings = [];
  const performance = {
    excellent: 0, // <= 15 min
    good: 0, // 16-30 min
    fair: 0, // 31-60 min
    poor: 0, // > 60 min
  };

  bookings.forEach(booking => {
    const timeCalc = calculateBookingToPickupTime(booking);
    if (
      !timeCalc.error &&
      !timeCalc.isPending &&
      timeCalc.diffMins !== undefined
    ) {
      completedBookings.push(timeCalc.diffMins);

      // Count performance categories
      if (timeCalc.diffMins <= 15) performance.excellent++;
      else if (timeCalc.diffMins <= 30) performance.good++;
      else if (timeCalc.diffMins <= 60) performance.fair++;
      else performance.poor++;
    }
  });

  const averagePickupTime =
    completedBookings.length > 0
      ? Math.round(
          completedBookings.reduce((sum, time) => sum + time, 0) /
            completedBookings.length
        )
      : 0;

  return {
    totalBookings: bookings.length,
    completedBookings: completedBookings.length,
    averagePickupTime,
    performance,
    performancePercentage: {
      excellent: completedBookings.length
        ? Math.round((performance.excellent / completedBookings.length) * 100)
        : 0,
      good: completedBookings.length
        ? Math.round((performance.good / completedBookings.length) * 100)
        : 0,
      fair: completedBookings.length
        ? Math.round((performance.fair / completedBookings.length) * 100)
        : 0,
      poor: completedBookings.length
        ? Math.round((performance.poor / completedBookings.length) * 100)
        : 0,
    },
  };
};

/**
 * Format time duration for display
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted string
 */
export const formatDuration = minutes => {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;

  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
};
